import { Link } from "react-router-dom";
import { ArrowLeft, Database, Users } from "lucide-react";

import { LandingShell } from "../components/LandingShell";
import { useOverview } from "../hooks/useClients";
import type { Client } from "../types";

export function ClientsPage() {
  const { data: overview, isLoading, error } = useOverview();
  const clients = overview?.clients ?? [];
  const totalClients = overview?.totalClients ?? 0;
  const totalDatasets = overview?.totalDatasets ?? 0;

  return (
    <LandingShell active="clients" breadcrumb="Clients" searchPlaceholder="Rechercher clients...">
      <div className="page-container">
        <Link to="/" className="back-link" aria-label="Retour à l’accueil">
          <ArrowLeft size={18} aria-hidden="true" />
          Retour à l'accueil
        </Link>

        <header className="page-header">
          <div className="page-header-content">
            <h1 className="clients-page-title">Clients</h1>
            <p className="page-subtitle">
              Choisissez un client pour explorer ses capteurs, jeux de données et visualisations.
            </p>
          </div>
        </header>

        <section className="stats-grid" aria-label="Statistiques clients">
          <article className="stat-card">
            <div className="stat-card-header">
              <div>
                <p className="stat-label">Total clients</p>
                <p className="stat-value">{totalClients}</p>
              </div>
              <span className="stat-card-icon" aria-hidden="true">
                <Users size={22} />
              </span>
            </div>
          </article>
          <article className="stat-card">
            <div className="stat-card-header">
              <div>
                <p className="stat-label">Datasets actifs</p>
                <p className="stat-value">{totalDatasets}</p>
              </div>
              <span className="stat-card-icon" aria-hidden="true">
                <Database size={22} />
              </span>
            </div>
          </article>
        </section>

        {isLoading ? <p className="loading-message">Chargement des clients…</p> : null}
        {error ? (
          <p role="alert" className="dashboard-error">
            Erreur : {error.message}
          </p>
        ) : null}

        {!isLoading && !error && clients.length > 0 ? (
          <section className="clients-grid" aria-label="Liste des clients">
            {clients.map((client) => (
              <ClientCard key={client.id} client={client} />
            ))}
          </section>
        ) : null}

        {!isLoading && !error && clients.length === 0 ? <p className="empty-state">Aucun client trouvé.</p> : null}
      </div>
    </LandingShell>
  );
}

type ClientCardProps = {
  client: Client;
};

function ClientCard({ client }: ClientCardProps) {
  const datasets = client.datasets ?? [];

  return (
    <article className="client-card-compact">
      <div className="client-card-compact-header">
        <div className="client-card-title-section">
          <Link
            to={`/clients/${encodeURIComponent(client.id)}`}
            className="client-card-link"
            aria-label={`Ouvrir la page client ${client.name}`}
          >
            <h2 className="client-card-name">{client.name}</h2>
          </Link>
        </div>
        <span className="badge badge-blue badge-lg">
          {client.datasetCount} dataset{client.datasetCount > 1 ? "s" : ""}
        </span>
      </div>

      <div className="client-datasets-section">
        <p className="section-label">Datasets disponibles</p>

        {datasets.length > 0 ? (
          <div className="datasets-list">
            {datasets.map((dataset) => (
              <Link
                key={dataset.id}
                to={`/clients/${encodeURIComponent(client.id)}/datasets/${encodeURIComponent(dataset.id)}`}
                className="dataset-list-item"
              >
                <div className="dataset-list-info">
                  <strong className="dataset-list-name">{dataset.name}</strong>
                  <span className="dataset-list-meta">{dataset.id}</span>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <p className="loading-text">Aucun dataset disponible.</p>
        )}
      </div>
    </article>
  );
}
